/**
 * A lever is one thing we could do and a claim about who it moves. The editor
 * keeps that claim explicit: named actors with a step on the −2…+2 scale, or a
 * whole category at once, each switchable so combinations can be compared.
 */

import React, { useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Plus, Trash2, UserPlus, Users, X } from 'lucide-react';
import { Button, IconButton, Input, Switch, Textarea } from '../../../components/ui';
import type { ScenarioLever } from '../../../types/issues';
import type { MapGraphNode } from '../map/mapModel';
import { categoryLabel } from '../map/mapModel';
import { positionStyle } from '../map/position';
import { StanceGlyph } from '../map/PositionScale';
import { leverSummary, newId, signedDelta } from './scenarioModel';

import { foldText } from '../../../utils/fuzzySearch';
const DELTAS = [-2, -1, 1, 2];

/**
 * How far this lever shifts someone, on the same five-step scale the board
 * uses — so "+1" means exactly one step towards us, not a vague nudge.
 */
export const DeltaPicker: React.FC<{
  value: number;
  onChange: (delta: number) => void;
  label: string;
  className?: string;
}> = ({ value, onChange, label, className = '' }) => (
  <div role="radiogroup" aria-label={label} className={`inline-flex items-center gap-0.5 rounded-full bg-neutral-100 p-0.5 ${className}`}>
    {DELTAS.map((delta) => {
      const active = delta === value;
      const style = positionStyle(delta);
      return (
        <button
          key={delta}
          type="button"
          role="radio"
          aria-checked={active}
          title={`${signedDelta(delta)} ${Math.abs(delta) === 1 ? 'step' : 'steps'} ${delta > 0 ? 'towards us' : 'away from us'}`}
          onClick={() => onChange(delta)}
          className={`h-6 w-7 rounded-full text-[11px] tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 ${
            active ? `${style.pill} font-medium` : 'text-neutral-500 hover:text-ink'
          }`}
        >
          {signedDelta(delta)}
        </button>
      );
    })}
  </div>
);

/** Search the actors already on the map — moving someone off it projects nothing */
const ActorPicker: React.FC<{
  nodes: MapGraphNode[];
  exclude: Set<string>;
  onPick: (node: MapGraphNode) => void;
}> = ({ nodes, exclude, onPick }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const matches = useMemo(() => {
    const q = foldText(query);
    return nodes
      .filter((node) => !exclude.has(`${node.node.subject.type}:${node.node.subject.id}`))
      .filter((node) => !q || foldText(node.label).includes(q))
      .slice(0, 8);
  }, [nodes, exclude, query]);

  if (!open) {
    return (
      <Button
        size="xs"
        variant="ghost"
        onClick={() => {
          setOpen(true);
          window.setTimeout(() => inputRef.current?.focus(), 0);
        }}
      >
        <UserPlus className="h-3.5 w-3.5" strokeWidth={1.75} />
        Add actor
      </Button>
    );
  }

  return (
    <div className="mt-1">
      <div className="flex items-center gap-1.5">
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search actors on this issue"
          aria-label="Search actors"
          className="h-8 text-[12px]"
        />
        <IconButton label="Cancel" size="sm" onClick={() => { setOpen(false); setQuery(''); }}>
          <X className="h-4 w-4" strokeWidth={1.75} />
        </IconButton>
      </div>
      <ul className="mt-1 max-h-44 overflow-y-auto rounded-xl border border-neutral-200 bg-white">
        {matches.length === 0 ? (
          <li className="px-3 py-2 text-[12px] text-neutral-400">No one left to add</li>
        ) : (
          matches.map((node) => (
            <li key={node.id}>
              <button
                type="button"
                onClick={() => {
                  onPick(node);
                  setQuery('');
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left transition-colors hover:bg-neutral-50"
              >
                <StanceGlyph position={node.node.position} />
                <span className="min-w-0 flex-1 truncate text-[12.5px] text-ink">{node.label}</span>
                <span className="flex-shrink-0 text-[11px] tabular-nums text-neutral-400">{Math.round(node.influence * 100)}</span>
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );
};

export const LeverCard: React.FC<{
  lever: ScenarioLever;
  nodes: MapGraphNode[];
  categories: string[];
  /** Named by the last projection as having changed nothing */
  ineffective?: boolean;
  onChange: (lever: ScenarioLever) => void;
  onRemove: () => void;
}> = ({ lever, nodes, categories, ineffective, onChange, onRemove }) => {
  const [open, setOpen] = useState(!lever.label);
  const effects = lever.effects || [];
  const segments = lever.segmentEffects || [];
  const taken = new Set(effects.map((e) => `${e.subjectType}:${e.subjectId}`));
  const spare = categories.filter((c) => !segments.some((s) => s.category === c));

  const patch = (changes: Partial<ScenarioLever>) => onChange({ ...lever, ...changes });

  return (
    <div className={`rounded-xl border ${lever.enabled ? 'border-neutral-200' : 'border-neutral-200/60 bg-neutral-50/50'}`}>
      <div className="flex items-start gap-2 px-3 py-2.5">
        <Switch
          checked={lever.enabled}
          onChange={(checked) => patch({ enabled: checked })}
          label={`${lever.label || 'Lever'} on`}
          className="mt-0.5"
        />
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="min-w-0 flex-1 text-left"
        >
          <span className={`block truncate text-[13px] leading-tight ${lever.enabled ? 'text-ink' : 'text-neutral-400'}`}>
            {lever.label || 'Untitled lever'}
          </span>
          <span className="mt-0.5 block truncate text-[11.5px] text-neutral-500">
            {leverSummary(lever)}
            {lever.cost ? ` · ${lever.cost}` : ''}
          </span>
          {ineffective && lever.enabled && (
            <span className="mt-1 inline-block rounded-full bg-amber-50 px-1.5 py-0.5 text-[10.5px] text-amber-700">
              Changed nothing
            </span>
          )}
        </button>
        <span className="flex flex-shrink-0 items-center">
          <IconButton label={open ? 'Collapse' : 'Edit lever'} size="xs" onClick={() => setOpen((v) => !v)}>
            {open ? <ChevronUp className="h-4 w-4" strokeWidth={1.75} /> : <ChevronDown className="h-4 w-4" strokeWidth={1.75} />}
          </IconButton>
          <IconButton label="Remove lever" size="xs" onClick={onRemove}>
            <Trash2 className="h-4 w-4" strokeWidth={1.75} />
          </IconButton>
        </span>
      </div>

      {open && (
        <div className="space-y-3 border-t border-neutral-200/70 px-3 py-3">
          <Input
            value={lever.label}
            onChange={(e) => patch({ label: e.target.value })}
            placeholder="Raise resettlement compensation"
            aria-label="Lever name"
          />
          <Textarea
            value={lever.description || ''}
            onChange={(e) => patch({ description: e.target.value || null })}
            placeholder="What it actually is, in a sentence"
            aria-label="Lever description"
            rows={2}
          />
          <Input
            value={lever.cost || ''}
            onChange={(e) => patch({ cost: e.target.value || null })}
            placeholder="Cost — e.g. $2.4m, or a seat on the board"
            aria-label="Cost"
          />

          <div>
            <p className="mb-1.5 text-[12px] text-neutral-500">Who it moves</p>
            {effects.length === 0 && segments.length === 0 && (
              <p className="mb-1.5 text-[12px] text-neutral-400">Nobody yet — a lever with no claim projects nothing.</p>
            )}
            <ul className="space-y-1.5">
              {effects.map((effect, index) => (
                <li key={`${effect.subjectType}:${effect.subjectId}`} className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-[12.5px] text-ink">{effect.subjectLabel}</span>
                  <DeltaPicker
                    label={`Move ${effect.subjectLabel} by`}
                    value={effect.delta}
                    onChange={(delta) => {
                      const next = effects.slice();
                      next[index] = { ...effect, delta };
                      patch({ effects: next });
                    }}
                  />
                  <IconButton
                    label={`Remove ${effect.subjectLabel}`}
                    size="xs"
                    onClick={() => patch({ effects: effects.filter((_, i) => i !== index) })}
                  >
                    <X className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </IconButton>
                </li>
              ))}
            </ul>
            <ActorPicker
              nodes={nodes}
              exclude={taken}
              onPick={(node) =>
                patch({
                  effects: [
                    ...effects,
                    {
                      subjectType: node.node.subject.type,
                      subjectId: node.node.subject.id,
                      subjectLabel: node.label,
                      delta: 1,
                      note: null,
                    },
                  ],
                })
              }
            />
          </div>

          {(segments.length > 0 || spare.length > 0) && (
            <div>
              <p className="mb-1.5 flex items-center gap-1.5 text-[12px] text-neutral-500">
                <Users className="h-3.5 w-3.5 text-neutral-400" strokeWidth={1.75} />
                Whole categories
              </p>
              <ul className="space-y-1.5">
                {segments.map((segment, index) => (
                  <li key={segment.category} className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-[12.5px] text-ink">{categoryLabel(segment.category)}</span>
                    <DeltaPicker
                      label={`Move ${categoryLabel(segment.category)} by`}
                      value={segment.delta}
                      onChange={(delta) => {
                        const next = segments.slice();
                        next[index] = { ...segment, delta };
                        patch({ segmentEffects: next });
                      }}
                    />
                    <IconButton
                      label={`Remove ${categoryLabel(segment.category)}`}
                      size="xs"
                      onClick={() => patch({ segmentEffects: segments.filter((_, i) => i !== index) })}
                    >
                      <X className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </IconButton>
                  </li>
                ))}
              </ul>
              {spare.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {spare.slice(0, 6).map((category) => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => patch({ segmentEffects: [...segments, { category, delta: 1, note: null }] })}
                      className="inline-flex h-6 items-center gap-1 rounded-full bg-neutral-100 px-2 text-[11.5px] text-neutral-600 transition-colors hover:bg-neutral-200"
                    >
                      <Plus className="h-3 w-3" strokeWidth={1.75} />
                      {categoryLabel(category)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const LeverList: React.FC<{
  levers: ScenarioLever[];
  nodes: MapGraphNode[];
  categories: string[];
  ineffective: string[];
  onChange: (levers: ScenarioLever[]) => void;
}> = ({ levers, nodes, categories, ineffective, onChange }) => {
  const dead = new Set(ineffective.map((name) => name.toLowerCase()));
  return (
    <div className="space-y-2">
      {levers.map((lever, index) => (
        <LeverCard
          key={lever.id}
          lever={lever}
          nodes={nodes}
          categories={categories}
          ineffective={dead.has((lever.label || '').toLowerCase()) || dead.has(lever.id.toLowerCase())}
          onChange={(next) => onChange(levers.map((l, i) => (i === index ? next : l)))}
          onRemove={() => onChange(levers.filter((_, i) => i !== index))}
        />
      ))}
      <Button
        size="sm"
        variant="secondary"
        onClick={() => onChange([...levers, { id: newId(), label: '', description: null, cost: null, enabled: true, effects: [], segmentEffects: [] }])}
      >
        <Plus className="h-4 w-4" strokeWidth={2} />
        Add lever
      </Button>
    </div>
  );
};
